import { Link, useParams } from 'react-router-dom'
import { getAccount, fmtMoney, fmtPct, pnlClass } from '../data/demo'
import { Page } from '../components/Layout'
import { AllocationBars, EquityChart, Metric, MonthlyHeatmap } from '../components/ui'

export function ProofPage() {
  const { id = 'grok-alpha' } = useParams()
  const account = getAccount(id === 'grok-alpha' ? 'grok-alpha' : id)

  function share() {
    const url = window.location.href
    if (navigator.share) {
      void navigator.share({ title: `Atlas Proof · ${account.name}`, url }).catch(() => {
        void navigator.clipboard.writeText(url)
        alert('Proof link copied.')
      })
    } else {
      void navigator.clipboard.writeText(url)
      alert('Proof link copied to clipboard.')
    }
  }

  function printPage() {
    window.print()
  }

  return (
    <Page wide>
      <div className="app-header">
        <div>
          <div className="chip-row" style={{ marginBottom: 8 }}>
            <span className="badge demo">Fictional development data</span>
            <span className="badge verified">Verified · read-only</span>
            <span className="badge">Age {account.trackRecordDays}d</span>
            <span className="badge">Fresh {account.freshnessMinutes}m</span>
          </div>
          <h1>Atlas Proof</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Public performance page for <strong>{account.name}</strong> · permalink concept
          </p>
        </div>
        <div className="chip-row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={share}>
            Share link
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={printPage}>
            Print / PDF
          </button>
          <Link to="/dashboard" className="btn btn-primary btn-sm">
            Open tracker
          </Link>
        </div>
      </div>

      <div className="notice warn" role="note">
        Explicit disclosure: this page displays fictional DEMO statistics for product development. It is not a live
        broker statement, not a regulated performance report, and not an offer to investors.
      </div>

      <div className="proof-hero" style={{ marginTop: '1rem' }}>
        <div className="card card-pad">
          <div className="cc-top">
            <div>
              <div className="mute mono" style={{ fontSize: '0.8rem' }}>
                {account.broker} · {account.platform} · {account.currency}
              </div>
              <h2 className="display" style={{ margin: '0.35rem 0', fontSize: '1.6rem' }}>
                {account.name}
              </h2>
            </div>
            <div className={`mono ${pnlClass(account.netReturnPct)}`} style={{ fontSize: '1.8rem', fontWeight: 700 }}>
              {fmtPct(account.netReturnPct)}
            </div>
          </div>
          <EquityChart data={account.equityCurve} height={260} />
          <div className="grid-metrics" style={{ marginTop: '0.75rem' }}>
            <Metric label="Absolute return" value={fmtMoney(account.absoluteReturn)} tone="lime" />
            <Metric label="Max drawdown" value={fmtPct(-account.maxDrawdownPct)} tone="danger" />
            <Metric label="Sharpe / Sortino" value={`${account.sharpe.toFixed(2)} / ${account.sortino.toFixed(2)}`} />
            <Metric label="Profit factor" value={account.profitFactor.toFixed(2)} />
            <Metric label="Win rate" value={`${account.winRate}%`} />
            <Metric label="Recovery factor" value={account.recoveryFactor.toFixed(2)} />
          </div>
        </div>

        <div className="stack">
          <div className="embed-card">
            <div className="badge verified">Embed card preview</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', marginTop: 8 }}>{account.name}</div>
            <div className="mono lime" style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {fmtPct(account.netReturnPct)} net
            </div>
            <div className="mute">Max DD {fmtPct(-account.maxDrawdownPct)} · {account.trackRecordDays}d verified</div>
            <div className="mono mute" style={{ marginTop: 8, fontSize: '0.75rem' }}>
              atlas.proof/{account.id}
            </div>
          </div>

          <div className="card card-pad">
            <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
              Deposit / withdrawal accounting
            </h3>
            <Metric label="Deposits" value={fmtMoney(account.deposits)} />
            <Metric label="Withdrawals" value={fmtMoney(account.withdrawals)} />
            <Metric label="Net external capital" value={fmtMoney(account.netDeposit)} />
            <p className="mute" style={{ marginBottom: 0, fontSize: '0.85rem' }}>
              Net return compounds only trading P&amp;L. External cash moves are reconciled on the verification
              timeline.
            </p>
          </div>

          <div className="card card-pad">
            <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
              Verification timeline
            </h3>
            <div className="timeline">
              {account.verificationTimeline.map((e) => (
                <div className="timeline-item" key={e.date + e.event}>
                  <div className="timeline-dot" aria-hidden />
                  <div>
                    <div className="mono mute" style={{ fontSize: '0.78rem' }}>
                      {e.date}
                    </div>
                    <strong>{e.event}</strong>
                    <div className="muted">{e.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="split-2" style={{ marginTop: '1rem' }}>
        <div className="card card-pad">
          <h3 className="display" style={{ marginTop: 0, fontSize: '1.15rem' }}>
            Monthly performance
          </h3>
          <MonthlyHeatmap data={account.monthly} />
        </div>
        <div className="card card-pad">
          <h3 className="display" style={{ marginTop: 0, fontSize: '1.15rem' }}>
            Strategy allocation
          </h3>
          <AllocationBars
            items={account.strategyAllocation.map((s) => ({
              label: s.name,
              pct: s.pct,
              color: s.color,
            }))}
          />
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 className="display" style={{ marginTop: 0, fontSize: '1.15rem' }}>
          Methodology
        </h3>
        <ul className="muted">
          <li>Read-only feed; no trading credentials stored on this development site.</li>
          <li>Balance jumps classified as deposit, withdrawal, or trading P&amp;L via reconciliation rules.</li>
          <li>Drawdown measured on equity high-water mark intra-sample.</li>
          <li>Risk-adjusted stats use daily equity returns (demo methodology).</li>
          <li>Public permalink concept: <span className="mono">/proof/{account.id}</span></li>
        </ul>
        <p className="notice danger" style={{ marginBottom: 0 }}>
          Risk disclaimer: trading leveraged products involves substantial risk of loss. Past performance — especially
          fictional demo performance — does not indicate future results. Atlas is analytics software, not a broker,
          CTA, or investment adviser.
        </p>
      </div>
    </Page>
  )
}
